import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";

export default function BatchImageUploadOCR() {
  const [scopeType, setScopeType] = useState("po"); // po | consignment
  const [scopeId, setScopeId] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const runAiMatchAndUpload = async () => {
    setError("");
    setResults([]);

    if (!scopeId) {
      setError("Scope ID is required.");
      return;
    }
    if (!files.length) {
      setError("Please select one or more images.");
      return;
    }

    setLoading(true);
    try {
      const out = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("scope_type", scopeType);
        formData.append("scope_id", String(scopeId));
        formData.append("image", file);

        const res = await fetch("/api/ai_match_and_upload_listing_image", {
          method: "POST",
          body: formData,
        });

        const data = await res.json().catch(() => ({}));
        out.push({
          fileName: file.name,
          ok: !!data.ok,
          uploaded: !!data.uploaded,
          listing_id: data.listing_id,
          confidence: data.match?.confidence,
          extracted_name: data.match?.extracted?.card_name,
          extracted_number: data.match?.extracted?.card_number,
          reason: data.match?.reason || data.reason || data.error || "",
        });
      }
      setResults(out);
    } catch (e) {
      console.error(e);
      setError(e.message || "AI match failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        AI Match + Upload (Experimental)
      </Typography>

      <Typography variant="body2" sx={{ mb: 2, opacity: 0.8 }}>
        Uses AI vision to match each photo to a listing inside a PO or Consignment session, then uploads the image to that matched listing.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? <LinearProgress sx={{ mb: 2 }} /> : null}

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Scope</InputLabel>
          <Select
            value={scopeType}
            label="Scope"
            onChange={(e) => setScopeType(e.target.value)}
          >
            <MenuItem value="po">Purchase Order</MenuItem>
            <MenuItem value="consignment">Consignment Session</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={scopeType === "po" ? "PO ID" : "Consignment Session ID"}
          value={scopeId}
          onChange={(e) => setScopeId(e.target.value)}
          sx={{ minWidth: 220 }}
        />

        <Button variant="outlined" component="label">
          Select Images
          <input hidden type="file" multiple accept="image/*" onChange={handleFileChange} />
        </Button>

        <Button
          variant="contained"
          onClick={runAiMatchAndUpload}
          disabled={loading || !files.length || !scopeId}
        >
          AI Match & Upload
        </Button>
      </Box>

      {files?.length ? (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Selected: {files.length} image(s)
        </Typography>
      ) : null}

      {results?.length ? (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Results
          </Typography>
          <List dense>
            {results.map((r, idx) => (
              <ListItem key={idx} divider>
                <ListItemText
                  primary={`${r.fileName}  â†’  Listing ${r.listing_id ?? "?"}  (${typeof r.confidence === "number" ? r.confidence.toFixed(2) : "n/a"})`}
                  secondary={
                    (r.extracted_name || r.extracted_number)
                      ? `Extracted: ${r.extracted_name || ""} ${r.extracted_number || ""} ${r.uploaded ? "â€¢ Uploaded" : ""}`
                      : (r.reason || "")
                  }
                />
              </ListItem>
            ))}
          </List>
        </Box>
      ) : null}
    </Box>
  );
}

